/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Component } from 'react'
import { message, Icon, DatePicker, Button, Input, Checkbox } from 'antd';
import { get, first, set, orderBy, flatten, uniqBy, keyBy } from 'lodash';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import styles from './schedule.styles';
import { getSchedule, mergePdfs, signManifest, updateOrder } from '../../_shared/services/orders.service';
import { TableList, TableListHeader } from '../../_shared/table-list';
import Link from '../../_shared/link';
import { DriverOrderButton, OrderModal, AdminOrderButton, orderSearch } from '../orders/orders.component';
import { getConfig, getLicAddress, getLicName, getRawLicense, loadGPSID, isNoShowRoute, jparse, orderIsRejected, size, userHasRole, getUrlParams, orderHasManifest, getManifestUrl, getCopiesUrl, replaceURLWithHTMLLinks } from '../../actions/utils';
import { awaitAll } from '../../actions/awaitAll';
import colors from '../../_shared/colors';
import { CashPickupIcon, CashReturnIcon, RejReturnIcon } from '../../_shared/CustomIcons';
import { getDataFromGuid } from '../../_shared/driver-search';
import { projectOrdersForExt } from '../dashboard.actions';
import ScreenComponent from '../../_shared/hooks/useScreen';
import Row from '../../_shared/row';
import { CHECKINICON, OrderStatus, getStatusIcon } from '../../models/order';
import { MapModal } from '../map';
import { getMapStatesByDate } from '../../_shared/services/maps.service';
import { BlackoutBanner } from '../../_shared/blackout-banner';

const isCashReturn = (o) => {
  return get(o, 'data.order.orderType') === 'cash_return';
}

const isRejReturn = (o) => {
  return get(o, 'data.order.orderType') === 'rej_return';
}

export const getOrderTypeBasedOnDay = (order, date) => {
  const isPickup = (get(order, 'data.order.pickupDate') && moment(get(order, 'data.order.pickupDate')).isSame(date, 'day')) || get(order, 'data.order.orderType') === 'sameDay';
  return isPickup ? 'pickup' : 'delivery';
}

export const getCols = (_this) => {
  const isMobile = get(_this, 'props.screen.isMobile');
  const isDriver = userHasRole(2, _this.props.sState);
  // Card #256 round 7 — master switch for the whole payment-requirement feature
  // (Edit Settings > `orders.paymentRequirementEnabled`, default OFF). Read ONCE
  // per column build, not per row. This is the DRIVER-facing screen, so with the
  // switch off it is one of the last places the feature could have leaked.
  // `colors.orderRequiresPayment` is deliberately NOT self-gating — see the
  // comment above it in ../../_shared/colors.js.
  const paymentRequirementEnabled = !!getConfig('orders.paymentRequirementEnabled', false);

  let rows = [
    {
      title: 'Order ID',
      dataIndex: 'orderId',
      width: isMobile ? 100 : 180,
      order: 0,
      fixed: get(_this, 'state.isPrint') ? undefined : 'left',
      render: (t, row) => {
        const order = get(row, '__order', row);
        const checkedin = !!get(row, 'data.order.checkedin');
        const isCashPickup = get(order, 'data.order.orderType') === 'cash_pickup';
        const isCashReturnLeg = _this.isCashReturnLeg(order);
        const isRejReturnOrder = isRejReturn(order);
        // Render distinct icon for Rejection Returns vs Reconciliation Returns (Joel 2026-05-26).
        const isCashReturn = isCashReturnLeg || get(order, 'data.order.cashReturn') || isRejReturnOrder;
        const isCashReturnOnly = isCashReturn && !isRejReturnOrder;
        const isRejected = orderIsRejected(order);
        // Card #256 — orange Order ID + tag when payment is required-and-unrecorded,
        // and only while the master switch is on (round 7).
        const requiresPayment = paymentRequirementEnabled && colors.orderRequiresPayment(order);
        const hasNotes = !!get(row, 'data.order.notes');
        const statico = getStatusIcon(row);
        // Card #274 — sameDay orders are filed by the SERVER under their delivery
        // route (server/src/controllers/orders/index.js:369-370 explicitly excludes
        // sameDay from the pickup-route grouping), but getOrderTypeBasedOnDay(:33-36)
        // returns 'pickup' for every sameDay order. That mismatch printed the pickup
        // leg's driver on a row grouped by the delivery leg: blank where
        // driver.pickup was never written (autoApprove off — the admin form hides
        // Pickup Route Driver for sameDay), stale/wrong where auto-approval stamped
        // it at creation and nothing ever kept it in sync. Read the delivery leg so
        // the name matches the route header the row sits under. getOrderTypeBasedOnDay
        // itself is left alone on purpose — :982 is the `same day pickups: N` counter
        // and legitimately wants 'pickup' for sameDay.
        const isSameDayOrder = get(order, 'data.order.orderType') === 'sameDay';
        const otype = (!_this.notCashReturn(order) || isSameDayOrder) ? 'delivery' : getOrderTypeBasedOnDay(row, _this.state.selectedDate)
        const { driver } = getDataFromGuid(get(row, `data.order.driver.${otype}`));
        const { hasFile } = orderHasManifest(row);
        const rl = getRawLicense(get(order, 'data.pickupCompany.Licensee'));
        const manireq = !!get(rl, 'manireq');

        return (
          <div>
            <Link onClick={() => _this.selectOrder(order, row)}>
              <span style={isRejected ? { color: colors.error, fontWeight: 'bold' } : requiresPayment ? { color: colors.warning, fontWeight: 'bold' } : checkedin ? { color: colors.highlight, fontWeight: 'bold' } : {}}>
                {t}
                {requiresPayment && <span title="Payment required before this order can be completed" style={{ marginLeft: 6, padding: '0 6px', border: `1px solid ${colors.warning}`, color: colors.warning, borderRadius: 3, fontSize: 11, fontWeight: 'bold', whiteSpace: 'nowrap' }}>Payment Required</span>}
                {statico && <Icon type={statico.icon} style={{ color: statico.color, marginLeft: 2 }} title={statico.title} />}
                {isRejReturnOrder && <RejReturnIcon />}
                {isCashReturnOnly && <CashReturnIcon />}
                {isCashPickup && !isCashReturn && <CashPickupIcon />}
                {checkedin && <Icon type={CHECKINICON} style={{ marginLeft: 2 }} />}
                {hasNotes && <Icon type="tag" title="Order has notes" style={{ marginLeft: 2 }} />}
                {hasFile && <Icon type="file-protect" title="Order has manifest uploaded" style={{ marginLeft: 2 }} />}
                {/* {!isDriver && manireq && <Icon type="file-protect" title="Manifest Required" style={{ color: 'blue' }} />} */}
              </span>
            </Link>
            {!isDriver && !_this.isDrop && <span>{get(driver, 'name')}</span>}
          </div>
        )
      }
    },
    {
      title: isMobile ? 'Quantity/\nNotes' : 'Quantity/Notes',
      dataIndex: 'data.order.notes',
      width: isMobile ? undefined : '15%',
      order: 10,
      render: (t, row) => {
        const linkedText = replaceURLWithHTMLLinks(t);

        return <span dangerouslySetInnerHTML={{ __html: linkedText }} />
      }
    },
    {
      title: 'Vendor',
      dataIndex: 'data.pickupCompany.Licensee',
      width: isMobile ? '15%' : '20%',
      order: 1,
      render: (t, row) => {
        const key = get(row, 'data.order.pickupLN');
        const comp = getRawLicense(key);
        const addr = getLicAddress(key);
        const compName = getConfig('schedule.useDBA', true) ? get(comp, `DBA`) || get(comp, `Licensee`) || '' : get(comp, `Licensee`) || '';
        const text = `${compName || ''}${compName ? ' - ' : ''}${addr}`;
        const isCashReturnLeg = (_this.isCashReturnLeg(row) || orderIsRejected(row) || isRejReturn(row)) && get(row, 'data.order.orderType') !== 'cash_pickup';

        if (isCashReturnLeg) {
          return <span>{getLicName(loadGPSID())}</span>
        }

        return <span>{text}</span>
      }
    },

    //#region reports only stuff
    {
      title: 'Destination Name',
      dataIndex: 'data.destCompany.Street Address',
      width: '14%',
      show: !!_this.isReport,
      value: (row) => {
        const isPickup = get(row, 'isPickup');
        const key = get(row, `data.order.${isPickup ? 'pickupLN' : 'destinationLN'}`);
        const { Licensee } = getRawLicense(key, { DBA: '' });

        //Reports uses this
        return `${Licensee}`;
      },
    },
    {
      title: 'DBA',
      dataIndex: 'data.destCompany.Street Address',
      width: '14%',
      show: !!_this.isReport,
      value: (row) => {
        const isPickup = get(row, 'isPickup');
        const key = get(row, `data.order.${isPickup ? 'pickupLN' : 'destinationLN'}`);
        const { DBA,  } = getRawLicense(key, { DBA: '' });

        //Reports uses this
        return DBA || '';
      },
    },
    {
      title: 'Destination Address',
      dataIndex: 'data.destCompany.Street Address',
      width: '14%',
      show: !!_this.isReport,
      value: (row) => {
        const isPickup = get(row, 'isPickup');
        const key = get(row, `data.order.${isPickup ? 'pickupLN' : 'destinationLN'}`);

        const address = getLicAddress(key, ['Street Address', 'City']);

        //Reports uses this
        return address || '';
      },
    },
    {
      title: 'Destination Zip',
      dataIndex: 'data.destCompany.Street Address',
      width: '14%',
      show: !!_this.isReport,
      value: (row) => {
        const isPickup = get(row, 'isPickup');
        const key = get(row, `data.order.${isPickup ? 'pickupLN' : 'destinationLN'}`);

        const map = getRawLicense(key);
        const zip = get(map, 'Zip');

        //Reports uses this
        return zip || '';
      },
    },
    {
      title: 'Destination License #',
      dataIndex: 'data.destCompany.Street Address',
      width: '14%',
      show: !!_this.isReport,
      value: (row) => {
        const isPickup = get(row, 'isPickup');
        const key = get(row, `data.order.${isPickup ? 'pickupLN' : 'destinationLN'}`);

        return key || '';
      },
    },
    //#endregion

    {
      title: 'Destination',
      dataIndex: 'data.destCompany.Street Address',
      width: isMobile ? '15%' : '20%',
      order: 2,
      show: !_this.isReport,
      render: (t, row) => {
        const isPickup = get(row, 'isPickup');
        const islayover = get(row, 'data.order.pickupType') === 'layoverpickup';
        const isCashReturnLeg = _this.isCashReturnLeg(row);
        const key = isPickup && islayover ? loadGPSID() : get(row, `data.order.${isPickup ? 'pickupLN' : 'destinationLN'}`);

        let { DBA, Licensee, intNotes } = getRawLicense(key, { DBA: '' });

        if (!getConfig('schedule.useDBA')) {
          DBA = undefined;
        }

        const address = getLicAddress(key);

        const text = `${Licensee}${DBA ? ` - ${DBA} - ` : ' - '}${address}`;
        
        return (
          <div>
            <span>{text}</span>
            {!!intNotes && <div style={{ fontWeight: 'bold' }}>{intNotes}</div>}
          </div>
        )
      }
    },
    {
      title: 'City',
      dataIndex: 'data.destCompany.City',
      width: '5%',
      order: 3,
      show: !isDriver,
      render: (t, row) => {
        const isPickup = get(row, 'isPickup');
        const key = isPickup || _this.isCashReturnLeg(row) ? get(row, 'data.order.pickupLN') : get(row, 'data.order.destinationLN');
        const comp = getRawLicense(key);
        return <span>{get(comp, 'City') || ''}</span>
      }
    },
    //driver blanks
    {
      title: 'Invoice',
      dataIndex: 'data.billing.invoice',
      width: isMobile ? undefined : '7%',
      order: 4,
    },
    {
      title: 'Amount',
      dataIndex: 'data.billing.amount',
      width: isMobile ? undefined : '7%',
      order: 5,
    },
    {
      title: 'Method',
      dataIndex: 'data.billing.method',
      width: isMobile ? undefined : '7%',
      order: 6,
    },
    {
      title: 'Manifest',
      dataIndex: 'data.billing.manifest',
      width: isMobile ? undefined : '7%',
      order: 7,
    },
    {
      title: 'Box Count',
      dataIndex: 'data.order.weight',
      width: isMobile ? undefined : '7%',
      order: 8,
    },
    {
      title: 'Delivery Invoice',
      dataIndex: 'data.billing.gpsinvoice',
      width: isMobile ? undefined : '7%',
      show: !isDriver && get(_this, 'state.showExtras', false),
      order: 9,
    },
    {
      title: 'Paperwork',
      dataIndex: 'data.billing.gpsinvoice',
      width: isMobile ? undefined : '7%',
      show: !isDriver && !get(_this, 'state.showExtras', false),
      order: 10,
      render: (t, row) => {
        const key = _this.getPaperDateKey();
        return <Checkbox checked={!!get(row, `data.order.paper.${key}`)} onChange={v => _this.changePaperVal(row, v)} />
      }
    },
  ].filter(f => get(f, 'show', true));

  if (isDriver) {
    rows = orderBy(rows, 'order');
  }

  return rows;
}

const order = [
  OrderStatus.orderReceived,
  OrderStatus.orderApproved,
  OrderStatus.orderPickedUp,
  OrderStatus.inTransit,
  OrderStatus.delivered,
  OrderStatus.exception,
  OrderStatus.fullrejection,
  OrderStatus.partialrejection,
]

const sortByDest = (a, b) => {
  const aval = order.indexOf(get(a, 'data.order.status', 1));
  const bval = order.indexOf(get(b, 'data.order.status', 1));
  const adest = get(a, 'data.order.destinationLN', '');
  const bdest = get(b, 'data.order.destinationLN', '');

  if (aval == bval) { return adest == bdest ? 0 : adest.localeCompare(bdest, 'en', { numeric: true }) };

  return aval - bval;
  // const aisPickup = get(a, 'isPickup');
  // const bisPickup = get(b, 'isPickup');
  // const ad = get(a, `data.${aisPickup ? 'pickupCompany' : 'destCompany'}.DBA`, '');
  // const bd = get(b, `data.${bisPickup ? 'pickupCompany' : 'destCompany'}.DBA`, '');
  // return ad.localeCompare(bd, 'en', { numeric: true });
}

const sortByDestAndCash = (a, b) => {
  const aiscash = isCashReturn(a);
  const biscash = isCashReturn(b);

  if (aiscash) {
    return 1;
  } else if (biscash) {
    return -1;
  } else if (aiscash && biscash) {
    return 0;
  }

  return sortByDest(a, b);
}

const getDate = d => {
  return moment(d);
}

export const getMidDate = d => {
  if (d && typeof d === 'string' && d.includes('T')) {
    d = d.split('T')[0];
  }

  return moment(moment(d).startOf('day').format('YYYY-MM-DD')).add(12, 'hours');
}

// Card #276 pass 2 -- the mapstate id was `${routeId}_${selectedDate.startOf('day').toISOString()}`.
// `startOf('day')` is LOCAL, so the UTC string it produced depended on the viewer's offset:
// a Denver user (UTC-6 in summer) wrote `..._2026-08-28T06:00:00.000Z` while a Central user
// (UTC-5) wrote `..._2026-08-28T05:00:00.000Z` for the SAME route on the SAME day -- which is
// exactly what happens when support impersonates a Colorado client, and what happened on
// 2026-08-25 while this card was being filed. Two rows, two conflicting saved orderings, and
// sortOrders below took whichever one the database happened to return first.
//
// The id is now derived from the calendar day alone, so every viewer of a route+day computes
// the same id regardless of timezone or DST. Note this also stops mutating state: the old line
// called `.startOf('day')` directly on `this.state.selectedDate`, and moment mutates in place.
export const getMapStateDate = d => moment(d).format('YYYY-MM-DD');

// Rows already in the table are still addressed by their legacy, timezone-skewed id. Rather
// than orphan them (or require a data backfill), reuse the id of the row that already exists
// for this route+day; where the old bug left more than one, prefer the most recently saved,
// which is the dispatcher's latest intent. Only a route+day with no row at all gets a freshly
// minted canonical id, so no NEW duplicate can be created either.
//
// `day` is compared against the first 10 characters of the id suffix rather than the `date`
// column: the column arrives as a UTC-midnight timestamp and re-parsing it locally would
// reintroduce the very off-by-one-day this function exists to remove.
export const resolveMapState = (mapstates, routeId, day) => {
  const prefix = `${routeId}_`;

  const rows = (Array.isArray(mapstates) ? mapstates : []).filter(ms => {
    const id = `${get(ms, 'id', '')}`;
    if (!id.startsWith(prefix)) return false;
    return !day || id.slice(prefix.length, prefix.length + 10) === day;
  });

  return first(orderBy(rows, [ms => moment(get(ms, 'lastUpdated') || get(ms, 'created')).valueOf()], ['desc']));
}

// The "Layover Pickup" table does not draw a layover order as it is stored. On the pickup
// day it draws the pickup leg: re-pointed at the pickup route and flagged isPickup, which
// makes the Destination cell show the GPS warehouse and City show the vendor's city.
// Shared by that table and by the schedule search, so the search reads the row the table
// actually paints.
const asLayoverPickupRow = o => ({ __order: { ...o }, ...o, route: jparse(get(o, 'data.order.pickupRoute')), isPickup: true });

// The rows one route block lists, from its pullOutPickupTypes buckets: the block's counts
// and its map both read this list. Shared so the map can be built from the whole day's
// route with exactly the rows the block would list for it.
const routeRows = (buckets, isDrop) => isDrop ? buckets.dropoff : [
  ...buckets.regular,
  ...buckets.warehouse,
  ...buckets.layoverpickup,
  ...buckets.cashreturn,
  ...buckets.rejreturn,
  ...buckets.cashpickup,
];

// `cols` (card #277) is the column set the schedule's own tables are built from, so a
// search here reaches the resolved vendor/destination names, the driver line under each
// order id, the notes and the billing cells — not only the eight fields orderSearch
// knows by name. It is optional so the exported helper stays callable without a
// component instance.
//
// `rowsFor(order)` lists the rows that order is drawn as (card #277 round 4); the order
// is kept when any of them matches. A cell like Destination paints different text for
// the same order depending on which table it sits in, so searching only the stored order
// missed text that was on screen. Defaults to the stored order alone.
export const filterSchedule = (searchTerm = '', originalSchedule, cols, rowsFor = o => [o]) => {
  const passes = {};

  if (!searchTerm) { return originalSchedule }

  const val = (searchTerm || '').toLowerCase();

  Object.keys(originalSchedule).map(k => {
    const { route, orders } = originalSchedule[k];

    const _orders = orders.filter(o => size(orderSearch(rowsFor(o), val, { cols })) > 0);

    const rname = get(route, 'name', '').toLowerCase();
    if (rname.indexOf(val) > -1 || size(_orders)) {
      set(passes, k, {
        route,
        orders: size(_orders) ? _orders : orders,
      })
    }
  })

  return passes;
}

export class ScheduleComponent extends Component {
  state = {
    loadingSchedule: false,
    selectedDate: getMidDate(),
    originalSchedule: {},
    schedule: {},
    today: getMidDate(),
    printOnly: undefined,
    editModal: undefined,
    isPrint: false,
    isAdmin: !!['/manageSchedule', '/adminSchedule'].find(k => window.location.href.includes(k)),
    map: {},
    showExtras: false,
    paperLoad: {},
    searchTerm: '',
  }
  getPaperDateKey = () => {
    return moment(get(this, 'state.selectedDate')).format('YY-MM-DD');
  }
  changePaperVal = async (row, e) => {
    try {
      const val = !!get(e, 'target.checked');
      set(row, `data.order.paper.${this.getPaperDateKey()}`, val);
      await updateOrder(row);
    } catch (err) {
      message.error(err.toString());
    }
  }
  componentDidMount = () => {
    this.loadSchedule();

    const { id } = getUrlParams(window.location.href);
    if (!!id) {
      this.selectOrder({ id });
    }
  }
  onSelectDay = (date) => {
    this.setState({ selectedDate: date ? getDate(date) : null }, this.loadSchedule);
  }
  loadSchedule = async () => {
    try {
      const { selectedDate, isAdmin } = this.state;
      if(!selectedDate) return;

      this.setState({ loadingSchedule: true });
      const day = getMidDate(selectedDate);
      const [schedule, mapstates] = await Promise.all([
        getSchedule({ day, isAdmin }),
        getMapStatesByDate(day.toISOString()),
      ])

      for (let k in schedule) {
        const { orders } = schedule[k];

        set(schedule, [k, 'orders'], this.filterOrders(orders));
      }

      // Pass the day this response was fetched for, not whatever is selected by
      // the time it lands — a slow response for one day can arrive after the
      // user has already picked another.
      this.pushOrdersToExtension(schedule, selectedDate);

      // Re-apply the active search to the freshly loaded day (card #277 round 4), as the
      // Orders page does after a reload. Every reload comes through here: a date change,
      // the sync button, closing an order or the map. Before this, each of them put the
      // unfiltered day back while the search box still showed the term.
      this.setState({ loadingSchedule: false, schedule, originalSchedule: schedule, mapstates }, this.applySearch);
    } catch (err) {
      message.error(`Error: ${err.message}`);
      this.setState({ loadingSchedule: false, schedule: {}, originalSchedule: {} });
    }
  }
  // Card #101 — bridge the selected schedule day to the Chrome extension.
  // The extension reads orders from the persisted `order.orders` slice (via
  // localStorage). The generic Dashboard pull (retrieveData) is capped and not
  // day-scoped, so selecting a day on this screen had no effect on what the
  // extension synced. Here we flatten the day's schedule, de-dup by id, project
  // each order down to the streamlined field set the extension's METRC form-fill
  // consumes, and OVERWRITE `order.orders` so "Sync Orders" pulls exactly this
  // day. Applies to all Schedule-screen users (admins and drivers).
  pushOrdersToExtension = (schedule, forDate) => {
    try {
      if (!this.props.setOrders) { return; }
      const all = flatten(Object.keys(schedule || {}).map(k => get(schedule, [k, 'orders'], [])));
      const deduped = uniqBy(all, 'id');
      const projected = projectOrdersForExt(deduped);
      // The day travels with the orders so the extension can name what it holds.
      // A day with nothing scheduled still pushes — an empty list is the honest
      // answer for that day, and leaving the previous day's orders in place is
      // what made the extension look like it was ignoring the selection.
      // `forDate` is the day these orders were fetched for; reading the current
      // selection here would label a late response with the wrong day.
      const day = forDate || this.state.selectedDate;
      const scope = day ? getDate(day).format('L') : '';
      this.props.setOrders(projected, scope);
    } catch (err) {
      console.warn(err);
    }
  }
  isCashReturnLeg = (order) => {
    const isCashPickup = get(order, 'data.order.orderType') === 'cash_pickup';
    return isCashPickup && this.isSameDate(order, 'data.order.deliveryDate');
  }
  isSameDate = (o, sel) => {
    const compDate = get(o, sel);
    const same = getMidDate(compDate).isSame(getMidDate(this.state.selectedDate), 'day');
    return !compDate ? false : same;
  }
  filterFromConfig = (orders) => {
    const filtConf = getConfig('schedule.filter', {});

    return orders.filter(o => {
      const found = Object.keys(filtConf).find(k => get(o, k, '').includes(filtConf[k]));
      return !found;
    })
  }
  filterOrders = (orders, byType, opts = {}) => {
    opts = {
      driver: null,
      ...opts,
    }

    orders = this.filterFromConfig(orders);

    if (userHasRole(2, this.props.sState) || !!opts.driver) {
      const thisdriver = opts.driver || get(this, 'props.sState.user.driver');

      if (!thisdriver) {
        return [];
      }

      const did = get(thisdriver, 'id');

      return orders.filter(o => {
        const otype = get(o, 'data.order.orderType');
        if (otype === 'cash_pickup' || otype === 'cash_return' || otype === 'rej_return') {
          // cash_return / rej_return use `driver.delivery` (Return Route Driver); cash_pickup uses `driver.pickup`
          const cpDriver = (otype === 'cash_return' || otype === 'rej_return')
            ? get(o, 'data.order.driver.delivery') || get(o, 'data.order.driver.pickup')
            : get(o, 'data.order.driver.pickup') || get(o, 'data.order.driver.delivery');
          if (cpDriver) {
            return get(getDataFromGuid(cpDriver), '_driver') == did;
          }
          return get(o, 'userId') === get(this, 'props.sState.user.id');
        }

        const odriver = get(o, 'data.order.driver');
        const route = jparse(get(o, 'data.order.route'));

        if (!odriver) { return false; }

        const deldriver = getDataFromGuid(odriver.delivery);
        const pickupdriver = getDataFromGuid(odriver.pickup);

        const matchesdel = get(deldriver, '_driver') == did;
        const matchespick = get(pickupdriver, '_driver') == did;
        const isNoShow = isNoShowRoute(route);

        if (isNoShow) { return false; }
        
        if (byType) {
          if (byType === 'delivery') {
            return matchesdel;
          } else if (byType === 'pickup') {
            return matchespick;
          }
        }
        
        return matchesdel || matchespick;
      })
    }
    
    return orders;
  }
  pulloutPickups = () => {
    const { schedule } = this.state;
    
    const pickups = {};

    Object.keys(schedule).map(k => {
      const { orders } = schedule[k];
      orders.map(o => {
        const otype = get(o, 'data.order.orderType');
        const proute = get(o, 'data.order.pickupRoute')

        const pr = jparse(proute);
        if (get(pr, 'zipCode') == '00000' && get(pr, 'city', '').indexOf('Utility') === -1 && proute && this.isSameDate(o, 'data.order.pickupDate')) {
          set(pickups, pr.city, [
            ...get(pickups, pr.city, []),
            o
          ])
        } else if (otype === 'dropoff' && this.isSameDate(o, 'data.order.dropoffDate')) {
          const sel = 'Warehouse Drop';
          set(pickups, sel, [
            ...get(pickups, sel, []),
            o,
          ])
        }
      })
    })

    return pickups;
  }
  notCashReturn = o => {
    const ot = get(o, 'data.order.orderType');
    return ot !== 'cash_return' && ot !== 'rej_return';
  }
  pullOutPickupTypes = (orders) => {
    return {
      regular: orders.filter(o => {
        const ot = get(o, 'data.order.orderType');
        const ptype = get(o, 'data.order.pickupType') === 'regular' || (!get(o, 'data.order.pickupType') && ot !== 'dropoff' && ot !== 'cash_return' && ot !== 'cash_pickup' && ot !== 'rej_return');
        return ptype && this.isSameDate(o, 'data.order.deliveryDate') && this.notCashReturn(o);
      }),
      warehouse: this.filterOrders(orders, 'delivery').filter(o => {
        const ptype = get(o, 'data.order.pickupType');
        const otype = get(o, 'data.order.orderType');
        const isLayover = (ptype === 'warehouse' || ptype === 'layoverpickup' || (otype === 'dropoff' && !ptype) || otype === 'cash_return') && this.isSameDate(o, 'data.order.deliveryDate');
        return isLayover && this.notCashReturn(o) && otype !== 'cash_pickup';// || get(o, 'data.order.pickupType') === 'warehouse';
      }),
      // layover: orders.filter(o => {
      //   return get(o, 'data.order.pickupType') === 'layoverpickup' && moment(get(o, 'data.order.deliveryDate')).isSame(this.state.selectedDate, 'day');
      // }),
      layoverpickup: this.filterOrders(orders, 'pickup').filter(o => {
        return get(o, 'data.order.pickupType') === 'layoverpickup' && this.isSameDate(o, 'data.order.pickupDate') && this.notCashReturn(o) && get(o, 'data.order.orderType') !== 'cash_pickup';
      }).map(o => asLayoverPickupRow(o)),
      dropoff: (() => {
        return orders.filter(o => get(o, 'data.order.orderType') === 'dropoff' && this.isSameDate(o, 'data.order.dropoffDate'));
      })(),
      // Card #240 (Round 3 — Danielle 2026-06-02) — customer-pickup returns
      // have no return route/driver/date scheduled, so they must NOT appear
      // on the route Schedule. Exclude customerPickup from BOTH the
      // Reconciliation Return (cash_return) and Rejection Return (rej_return)
      // buckets — these arrays drive both the counts and the rendered
      // "Reconciliation Return" / "Rejection Return" sections.
      cashreturn: orders.filter(o => get(o, 'data.order.orderType') === 'cash_return' && !get(o, 'data.order.customerPickup')),
      rejreturn: orders.filter(o => get(o, 'data.order.orderType') === 'rej_return' && !get(o, 'data.order.customerPickup')),
      cashpickup: orders.filter(o => get(o, 'data.order.orderType') === 'cash_pickup' && this.isSameDate(o, 'data.order.pickupDate')),
    }
  }
  passesIgnore = (key) => {
    const ignore = ['Cash', 'Warehouse'];
    return size(ignore.filter(ig => key.indexOf(ig) > -1)) === 0;
  }
  getScheduleMap = (schedule = this.state.schedule) => {

    // const pickups = this.pulloutPickups();
    // const warehouses = Object.keys(pickups).map(k => pickups[k]).map(o => o.id);

    const sched = {
      DROPS: {
        route: { name: 'Dropoffs' },
        orders: [],
      }
    };

    Object.keys(schedule).map(k => {
      const item = schedule[k];
      const { layoverpickup, regular, warehouse, dropoff, cashreturn, rejreturn, cashpickup } = this.pullOutPickupTypes(item.orders);

      const routekey = 'route.id';
      const schedkey = get(item, routekey, k);

      // const layids = layoverpickup.map(lo => lo.id);
      const lps = [...layoverpickup, ...regular, ...warehouse, ...cashreturn, ...rejreturn, ...cashpickup];
      if (size(lps)) {
        // layoverpickup.filter(lp => warehouses.indexOf(lp.id) === -1).map(lo => {
        //   const routename = get(lo, routekey, 'Default');
        //   const sel = routename;
        //   const ordsel = `${sel}.orders`;
        //   set(sched, [sel, 'route'], lo.route);
        //   set(sched, [sel, 'isPickup'], true);
        //   set(sched, ordsel, [
        //     ...get(sched, ordsel, []),
        //     lo,
        //   ])
        // })
      
        const otherorders = get(sched, [schedkey, 'orders'], []);
        const orders = [...otherorders, ...item.orders];
        sched[schedkey] = { 
          ...item, 
          // orders: orders.filter(o => layids.indexOf(o.id) === -1) //we are filtering out layoverpickups here, for some reason it is removing some leaflink orders
          orders: uniqBy(orders, 'id'),// orders.filter(o => layids.indexOf(o.id) === -1) //we are filtering out layoverpickups here, for some reason it is removing some leaflink orders
        }
      }

      if (size(dropoff)) {
        const key = 'DROPS.orders';
        set(sched, key, [...get(sched, key, []), ...dropoff])
      }
    })

    return sched;
  }
  selectOrder = (editOrder, row) => {
    const orderType = get(editOrder, 'data.order.orderType');
    const cashReturn = orderType === 'cash_return' || orderType === 'cash_pickup' || orderType === 'rej_return';
    this.setState({
      editModal: {
        isModal: true,
        state: {
          cashReturn,
          isPickup: get(row, 'isPickup', false),
          editOrder,
        }
      }
    });
  }
  orderFromSchedule = (sched, cashReturn = false) => {
    this.props.history.push('/dashboard/adminEditOrder', {
      isNewAdminOrder: true,
      cashReturn,
      editOrder: cashReturn ? {} : {
        data: {
          ...get(first(sched.orders), 'data', {}),
        }
      },
    })
  }
  renderWarehouse = () => {
    const { loadingSchedule, selectedDate, printOnly, isPrint } = this.state;

    const pickups = this.pulloutPickups();

    if (!size(pickups) || loadingSchedule) { return null };

    const tProps = {
      implementScroll: false,
      columns: getCols(this),
      showPagination: false,
      canSelectRows: false,
      scroll: isPrint ? undefined : { x: 1000 }
    }

    return (
      <div css={css(styles.routeDivider)} className={printOnly && printOnly.indexOf('__warehouse__') === -1 ? 'no-print printNoMarg' : 'printNoMarg'}>
        <TableListHeader css={css(styles.routeTableHeader)}>
          <div css={css(styles.routeHeader)}>{`Warehouse - ${getDate(selectedDate).format('L')}`}</div>
        </TableListHeader>

        {Object.keys(pickups).map(k => {
          const orders = pickups[k];
          return (
            <div className={printOnly && printOnly !== `${k}__warehouse__` ? 'no-print printNoMarg' : "printNoMarg"} css={css(styles.margB10)}>
              <TableListHeader>
                <div className="margHeader" css={css(styles.pickTypeHeader)}>{k}</div>
                <div css={css(styles.printOnly)}>
                  <Button
                    onClick={() => this.setState({ printOnly: `${k}__warehouse__` }, this.print)}
                    css={css(styles.printOnly)}
                    className="no-print"
                    type="primary"
                    icon="printer"
                  />
                </div>
              </TableListHeader>
              <TableList {...tProps} data={orders.sort(sortByDest)} />
            </div>
          )
        })}
      </div>
    )
  }
  setupMap = async (items, k) => {
    const allOrders = items;

    const key = 'data.order.driver.delivery';
    const _driver = get(items.find(item => get(item, key)), key);
    const driver = _driver ? getDataFromGuid(_driver).driver : null;

    const driverOrders = this.filterOrders(allOrders, false, { driver });

    let addresses = [];
    const addMap = {};

    driverOrders.map(item => {
      const dln = get(item, 'data.order.destinationLN');
      const pln = get(item, 'data.order.pickupLN');

      const issameday = get(item, 'data.order.orderType') == 'sameDay';

      const isDelivery = issameday || getOrderTypeBasedOnDay(item, this.state.selectedDate) === 'delivery';
      const isPickup = getOrderTypeBasedOnDay(item, this.state.selectedDate) === 'pickup';

      const ln = isDelivery ? dln : pln;
      const status = get(item, 'data.order.status');

      if (issameday) {
        const thisraw = getRawLicense(pln);
        set(addMap, pln, [ 
          ...get(addMap, pln, []), 
          {
            name: getLicName(pln),
            address: getLicAddress(pln),
            zip: get(thisraw, 'Zip'),
            ln: pln,
            status,
            isSameDayPickup: true,
          } 
        ])
      }

      const raw = getRawLicense(ln);
      set(addMap, ln, [ 
        ...get(addMap, ln, []), 
        {
          name: getLicName(ln),
          address: getLicAddress(ln),
          zip: get(raw, 'Zip'),
          ln,
          status,
          isDelivery,
          isPickup: isPickup && !issameday,
        } 
      ])
    })

    addresses = orderBy(Object.keys(addMap).map(k => {
      const amt = addMap[k];
      const item = amt.find(a => a.isSameDayPickup) || amt[0];
      const samedaypickups = size(amt.filter(f => f.isSameDayPickup));;
      const deliverys = size(amt.filter(f => f.isDelivery));
      const pickups = size(amt.filter(f => f.isPickup));
      const raw = getRawLicense(item.ln);
      const status = amt.map(a => a.status);

      const name = [
        get(raw, 'City'),
        get(amt, '0.ln'),
        item.name,
        `same day pickups: ${samedaypickups}, pickups: ${pickups}, deliveries: ${deliverys}`
      ];

      return {
        ...amt[0],
        pickups,
        samedaypickups,
        status,
        name: name.filter(n => !!n).join(' - '),
        ln: get(amt, '0.ln'),
      }
    }), 'samedaypickups', 'desc');

    // Card #276 pass 2 -- see getMapStateDate / resolveMapState above.
    const date = getMapStateDate(this.state.selectedDate);
    const existing = resolveMapState(this.state.mapstates, k, date);
    const id = get(existing, 'id') || `${k}_${date}`;

    this.setState({ 
      map: {
        id,
        date,
        addresses,
      }
    });
  }
  signManifests = async (items, opts = {}) => {
    try {
      opts = {
        signIfAvailable: true,
        ...opts,
      }
      this.setState({ signing: true });
      const haveManifests = items;

      const signature = get(this.props, 'sState.user.signature');

      if (!signature && opts.signIfAvailable) {
        throw new Error('Please setup a signature before you use this');
      }

      let totalSigned = 0;
      let totalUpdated = 0;

      const wrongStats = [OrderStatus.delivered, OrderStatus.exception, OrderStatus.fullrejection, OrderStatus.partialrejection];
      const ordersNotRightState = haveManifests.filter(o => wrongStats.includes(get(o, 'data.order.status')));

      if (size(ordersNotRightState)) {
        const cont = window.confirm('Some of these orders have already been delivered or rejected. Are you sure you want to put these orders back into In-Transit.');
        if (!cont) {
          throw new Error('Sign manifests canceled.');
        }
      }

      await awaitAll(haveManifests.map(order => () => async () => {
        const hasMan = get(order, 'data.manifest')
        if (opts.signIfAvailable && hasMan) { 
          set(order, 'data.order.signature.driver', { ...signature, date: new Date().toISOString() });
        }
        set(order, 'data.order.status', OrderStatus.inTransit);
        await updateOrder(order);
        if (hasMan && opts.signIfAvailable) { 
          await signManifest({ order });
          totalSigned += 1;
        }
        totalUpdated += 1;
      }), { queueSize: 2 });

      if (opts.signIfAvailable) {
        message.success(`${totalSigned} manifest(s) out of ${size(items)} were signed`);
      }
      message.success(`${totalUpdated} orders out of ${size(items)} were set to In Transit`);
    } catch (err) {
      message.error(err.message);
    } finally {
      this.setState({ signing: false });
    }
  }
  sortOrders = (orders, k) => {
    // Card #276 pass 2 -- was `.find(ms => ms.id.startsWith(k))`, which picked an arbitrary
    // row whenever the timezone bug had written two for this route+day, so the printed
    // schedule could disagree with the map. Resolve the same row the map modal writes to.
    const mapstate = resolveMapState(get(this, 'state.mapstates', []), k, getMapStateDate(this.state.selectedDate));

    let ords = [];
    if (mapstate) {
      const addrs = orderBy(get(mapstate, 'data.addresses'), 'order');

      addrs.map(a => {
        const relorders = orders.filter(o => get(o, 'data.order.destinationLN') == a.ln || get(o, 'data.order.pickupLN') == a.ln);
        ords.push(relorders)
      })
      
      ords = uniqBy(flatten(ords), 'id');
      const keyed = keyBy(ords, 'id');

      orders.map(o => {
        const found = keyed[o.id];

        if (!found) {
          ords.push(o);
        }
      })

      const BOTTOM = [OrderStatus.delivered, OrderStatus.exception, OrderStatus.fullrejection, OrderStatus.orderCancelled, OrderStatus.partialrejection];
      
      ords = ords.sort((a, b) => {
        const astat = get(a, 'data.order.status');
        const bstat = get(b, 'data.order.status');

        if (BOTTOM.includes(astat)) {
          return 1;
        } else if (BOTTOM.includes(bstat)) { 
          return -1;
        }

        return 0;
      });

    } else {
      ords = orders.sort(sortByDest);
    }

    return ords;
  }
  renderSchedule = () => {
    const { loadingSchedule, printOnly, selectedDate, isPrint } = this.state;
    
    const isMobile = get(this, 'props.screen.isMobile');

    const schedule = this.getScheduleMap();

    if (loadingSchedule) {
      return <Icon type="loading" />
    }

    const isDriver = userHasRole(2, this.props.sState);

    const view = (
      <div>  
        {Object.keys(schedule).map(k => {
          const buckets = this.pullOutPickupTypes(get(schedule, [k, 'orders']));
          const { regular, warehouse, layoverpickup, cashreturn, rejreturn, cashpickup, dropoff } = buckets;

          if (!this.passesIgnore(k)) { return null }

          const isDrop = k === 'DROPS';

          if (isDrop && isDriver) { return null }

          const amt = routeRows(buckets, isDrop);

          const tProps = {
            implementScroll: false,
            columns: getCols({ ...this, isDrop }),
            showPagination: false,
            canSelectRows: false,
            scroll: isPrint ? undefined : { x: isMobile ? 850 : 1000 }
          }

          const sched = get(schedule, k);

          const mapsEnabled = !!getConfig('scheduleMapsEnabled');

          const count = {
            del: size(amt.filter(o => getOrderTypeBasedOnDay(o, selectedDate) === 'delivery' || get(o, 'data.order.orderType') === 'sameDay')),
            pick: size(amt.filter(o => getOrderTypeBasedOnDay(o, selectedDate) === 'pickup' && get(o, 'data.order.orderType') !== 'sameDay')),
            sameday: size(amt.filter(o => getOrderTypeBasedOnDay(o, selectedDate) === 'pickup' && get(o, 'data.order.orderType') === 'sameDay')),
          }

          return !size(amt) ? null : (
            <div key={k} css={css(styles.routeDivider)} className={printOnly && printOnly !== k ? 'no-print printNoMarg' : 'printNoMarg'}>
              <TableListHeader css={css(styles.routeTableHeader)}>
                <div css={css(styles.routeHeader)}>{`${get(sched, ['route', 'name'], '')} - ${getDate(selectedDate).format('L')}`}</div>
                <Row css={css(styles.printOnly, `width: auto;`)}>
                  <Button
                    onClick={() => this.setState({ printOnly: k }, this.print)}
                    css={css(styles.printOnly)}
                    className="no-print"
                    type="primary"
                    icon="printer"
                  />

                  <Button
                    onClick={() => this.printManifests(warehouse)}
                    css={css(styles.printOnly)}
                    className="no-print"
                    type="primary"
                    title="Print Manifests"
                  >
                    <Icon type="file-protect" />
                    <Icon type={this.state.isPrintManifests ? "loading" : "printer"} />
                  </Button>

                  {mapsEnabled && <Button
                    onClick={() => this.setupMap(this.mapRowsFor(k), k)}
                    css={css(styles.printOnly)}
                    className="no-print"
                    type="primary"
                    icon="compass"
                  />}

                  {getConfig('sigs.show') && isDriver && <Button
                    onClick={() => this.signManifests(warehouse)}
                    css={css(styles.printOnly)}
                    className="no-print"
                    type="primary"
                    icon="edit"
                    title="Sign Manifests"
                    loading={this.state.signing}
                  />}

                  <div style={{ marginLeft: 5, fontSize: 16 }}>
                    {`same day pickups: ${count.sameday}, pickups: ${count.pick}, deliveries: ${count.del}`}
                  </div>
                </Row>
              </TableListHeader>
              
              {size(regular) > 0 && <div className="printNoMarg" css={css(styles.margB10)}>
                <div className="margHeader" css={css(styles.pickTypeHeader)}>{"Same Day Pickup"}</div>
                <TableList {...tProps} data={this.sortOrders(regular, k)} />
              </div>}
              {size(warehouse) > 0 && <div className="printNoMarg" css={css(styles.margB10)}>
                <div className="margHeader" css={css(styles.pickTypeHeader)}>
                  {"Warehouse Pickup"}
                  {!getConfig('sigs.show') && <Button
                    onClick={() => this.signManifests(warehouse)}
                    css={css(styles.printOnly)}
                    className="no-print"
                    type="primary"
                    icon="car"
                    title="Set to In Transit"
                    loading={this.state.signing}
                  />}
                </div>
                <TableList {...tProps} data={this.sortOrders(warehouse, k)} />
              </div>}
              {size(layoverpickup) > 0 && <div className="printNoMarg" css={css(styles.margB10)}>
                <div className="margHeader" css={css(styles.pickTypeHeader)}>{"Layover Pickup"}</div>
                <TableList {...tProps} data={this.sortOrders(layoverpickup, k)} />
              </div>}
              {size(cashreturn) > 0 && <div className="printNoMarg" css={css(styles.margB10)}>
                <div className="margHeader" css={css(styles.pickTypeHeader)}>{"Reconciliation Return"}</div>
                <TableList {...tProps} data={this.sortOrders(cashreturn, k)} />
              </div>}
              {size(rejreturn) > 0 && <div className="printNoMarg" css={css(styles.margB10)}>
                <div className="margHeader" css={css(styles.pickTypeHeader)}>{"Rejection Return"}</div>
                <TableList {...tProps} data={this.sortOrders(rejreturn, k)} />
              </div>}
              {isDrop && size(dropoff) > 0 && <div className="printNoMarg" css={css(styles.margB10)}>
                {/* <div className="margHeader" css={css(styles.pickTypeHeader)}>{"Dropoffs"}</div> */}
                {/* Not sure this works with dropoffs */}
                <TableList {...tProps} _data={dropoff.sort(sortByDest)} data={this.sortOrders(dropoff, k)} /> 
              </div>}
              {size(cashpickup) > 0 && <div className="printNoMarg" css={css(styles.margB10)}>
                <div className="margHeader" css={css(styles.pickTypeHeader)}>{"Payment Pickup"}</div>
                <TableList {...tProps} data={this.sortOrders(cashpickup, k)} />
              </div>}
            </div>
          )
        })}
      </div>
    )

    // Card #277 round 4b — ask the route blocks themselves whether anything is listed. The
    // old check was size(getScheduleMap()) === 0, which could never be true: the map always
    // seeds a DROPS entry and size() counts keys, while a route with no rows renders null.
    // So an empty day, or a search matching nothing, painted a blank area instead of this.
    if (![].concat(view.props.children).some(block => !!block)) {
      return <div css={css(styles.noOrders)}>No Orders found. Please select a different date.</div>
    }

    return view;
  }
  // Card #277 round 4b — the rows the map for route `k` is built from: the WHOLE day's
  // route, never the search-filtered view on screen. MapModal saves the stop list it is
  // given over this route/day's map state (same id, full replace) as soon as it opens, and
  // again on every reorder, delete/restore and "Show Directions" toggle. Built from a
  // filtered view, that save dropped every hidden stop's order and deleted flag. With no
  // search active this is exactly the list the route block hands the map.
  mapRowsFor = k => {
    const isDrop = k === 'DROPS';
    const buckets = this.pullOutPickupTypes(get(this.getScheduleMap(this.state.originalSchedule), [k, 'orders'], []));

    // The Dropoffs block's row list IS its dropoff bucket, which its table sorts in place
    // (`_data={dropoff.sort(sortByDest)}`) before the map button can be clicked, so its map
    // has always been handed that order. Kept: setupMap draws the first listed order's
    // driver's stops, so the order decides whose route the Dropoffs map shows.
    if (isDrop) { buckets.dropoff.sort(sortByDest); }

    return routeRows(buckets, isDrop);
  }
  print = () => {
    this.setState({ isPrint: true }, () => {
      window.print();
      this.setState({ isPrint: false })
    })
  }
  printManifests = (orders) => {
    const missing = [];
    const manifests = orders.map(o => {
      const man = orderHasManifest(o)
      if (!man.hasFile) { missing.push(o); return null; }
      return {
        ...man,
        url: getManifestUrl(man.file),
      }
    }).filter(f => !!f);

    if (size(missing)) {
      const cont = window.confirm(`These orders are missing manifests:\n\n${missing.map(m => m.orderId).join('\n')}\n\nAre you sure you wish to continue?`);
      if (!cont) { return }
    }
    
    this.setState({ isPrintManifests: true }, async () => {
      try {
        const result = await mergePdfs({ pdfs: manifests.map(m => m.url) })
        const url = getCopiesUrl(result.data);
        window.open(url, '_blank');
      } catch (err) {
        console.error(err);
      } finally {
        this.setState({ isPrintManifests: false })
      }
    })
  }
  // Card #277 round 4 — the rows an order is drawn as on the selected day, for the search.
  // The stored order is always included, so nothing that matched before stops matching.
  // A layover order on its pickup day is also drawn as its Layover Pickup row, whose
  // Destination is the GPS warehouse rather than the order's destination. Without that
  // row, searching the warehouse zip shown in the cell returned nothing. The row comes from
  // pullOutPickupTypes itself, so it is only added when that table would really show it.
  searchRowsFor = o => [o, ...this.pullOutPickupTypes([o]).layoverpickup];
  applySearch = () => {
    const { searchTerm, originalSchedule } = this.state;
    this.setState({ schedule: filterSchedule(searchTerm, originalSchedule, getCols(this), this.searchRowsFor) })
  }
  // The term is the one last submitted (Enter or the search button). Submitting an empty
  // box clears it, and filterSchedule hands back the whole day.
  filterSchedule = (searchTerm = '') => {
    this.setState({ searchTerm }, this.applySearch);
  }
  onCloseEditModal = shouldUpdate => {
    this.setState({ editModal: undefined }, () => {
      if (shouldUpdate) { this.loadSchedule() }
    })
  }
  render() {
    const {
      loadingSchedule,
      schedule,
      selectedDate,
      editModal,
      showExtras,
    } = this.state;

    const canShowPrint = !this.props.screen.isMobile && schedule && size(schedule) > 0 && !loadingSchedule && selectedDate;

    const { isMobile } = this.props.screen;

    const isDriver = userHasRole(2, this.props.sState);

    return (
      <div className="scheduleWrap" css={css(styles.container)}>
        <div className="no-print" css={css(styles.calContainer, isMobile && `flex-direction: column;`)}>
          <Row style={!isMobile ? { width: '30%', marginRight: 10 } : { marginRight: 10, }}>
            <Input.Search
              placeholder={'Search all columns, or Route Name'}
              enterButton
              onSearch={this.filterSchedule}
            />
            <Button icon="sync" style={isMobile ? {} : { marginRight: 10, marginLeft: 10 }} className="no-print" type="primary" onClick={this.loadSchedule} loading={loadingSchedule} />
            {!isDriver && <Button icon="menu-fold" style={isMobile ? {} : { marginRight: 10, marginLeft: 10 }} className="no-print" type="primary" onClick={() => this.setState({ showExtras: !showExtras })} loading={loadingSchedule} />}
          </Row>
          <AdminOrderButton onClose={this.loadSchedule} />
          <DriverOrderButton onClose={this.loadSchedule} />
          <DatePicker 
            css={css(isMobile ? `margin: 5px;` : 'width: 40%; margin: 0 auto;')}
            defaultValue={selectedDate}
            loading={loadingSchedule}
            onChange={this.onSelectDay}
          />

          {canShowPrint && 
            <Button 
              onClick={() => this.setState({ printOnly: undefined }, this.print)} 
              css={css(styles.printBtn)} 
              className="no-print"
              type="primary" 
              icon="printer"
            >
              Print
            </Button>}
        </div>

        {/* Card #254 round 3 — an empty schedule on a blacked-out day is
            indistinguishable from a broken one, so say why. Covers all three
            routes that mount this component: /dashboard/schedule,
            /dashboard/manageSchedule and /dashboard/adminSchedule. Shown to
            every role that can reach the screen, drivers included — "the
            company is closed" is not privileged information. */}
        <BlackoutBanner dates={[selectedDate]} />

        <div css={css(styles.schedContainer)}>
          {this.renderSchedule()}
          {/* {this.renderWarehouse()} */}
        </div>
        <OrderModal editModal={editModal} onClose={this.onCloseEditModal} />
        <MapModal {...this.state.map} onClose={() => this.setState({ map: {} }, this.loadSchedule)} />
      </div>
    )
  }
}

export const Schedule = ScreenComponent(withRouter(ScheduleComponent));
